import { useState } from 'react';
import { Dialog } from '@elementary/common-ui/components';
import { REMOVE_CHANGEPOINT_BUTTON_LABEL } from '@elementary/common-ui/components/TestCharts/constants';
import { Typography } from '@mui/material';
import useSnackbar from 'shared/hooks/useSnackbar';
import {
  CHANGEPOINT_REMOVED_TOAST,
  DEFAULT_FEEDBACK_ERROR_MESSAGE,
  REMOVE_CHANGEPOINT_CONFIRM_MESSAGE,
} from '../constants';
import useDeleteFeedback from '../hooks/useDeleteFeedback';
import { TrackFeedbackEvent } from '../types';
import {
  getChangepointTrackingProperties,
  getFeedbackErrorResponse,
  isFeedbackNotFoundError,
} from '../utils';

type Props = {
  feedbackId: string;
  onClose: () => void;
  track: TrackFeedbackEvent;
};

const RemoveChangepointDialog = ({ feedbackId, onClose, track }: Props) => {
  const { openSnackbar } = useSnackbar();
  const { mutateAsync: deleteFeedback, isLoading } = useDeleteFeedback();
  const [errorMessage, setErrorMessage] = useState<string>();

  const feedbackProperties = getChangepointTrackingProperties(feedbackId);

  const handleCancel = () => {
    track('CHANGEPOINT_REMOVE_CANCELLED', feedbackProperties);
    onClose();
  };

  const handleRemove = async () => {
    track('CHANGEPOINT_REMOVE_CONFIRMED', feedbackProperties);
    setErrorMessage(undefined);
    try {
      await deleteFeedback(feedbackId);
      openSnackbar({ message: CHANGEPOINT_REMOVED_TOAST });
      onClose();
    } catch (error) {
      const response = getFeedbackErrorResponse(error);
      track('CHANGEPOINT_REMOVE_FAILED', {
        ...feedbackProperties,
        error_code: response?.elementary_error_code ?? null,
      });
      if (isFeedbackNotFoundError(error)) {
        onClose();
        return;
      }
      setErrorMessage(
        response?.error_message || DEFAULT_FEEDBACK_ERROR_MESSAGE,
      );
    }
  };

  return (
    <Dialog
      open
      onClose={handleCancel}
      title={REMOVE_CHANGEPOINT_BUTTON_LABEL}
      customWidth={440}
      cancelText="Cancel"
      onCancelButtonClick={handleCancel}
      confirmText="Remove"
      isLoading={isLoading}
      onConfirmButtonClick={handleRemove}
    >
      <Typography>{REMOVE_CHANGEPOINT_CONFIRM_MESSAGE}</Typography>
      {errorMessage && (
        <Typography sx={{ color: 'status.error.main', mt: 1 }}>
          {errorMessage}
        </Typography>
      )}
    </Dialog>
  );
};

export default RemoveChangepointDialog;
